// Simplified Book Schemas - Using hierarchical BookSection structure

/**
 * Print permission is per file, not per book or per lesson: a printable file
 * block (currently only `pdf`) carries `payload.viewer.allow_print`.
 * Mirrors modules/Book/Services/FilePrintSettings.php on the API.
 */
export const PRINTABLE_CONTENT_TYPES = ['pdf'] as const;

export const isPrintableContentType = (contentType?: string): boolean =>
  !!contentType && (PRINTABLE_CONTENT_TYPES as readonly string[]).includes(contentType);

export const isFilePrintAllowed = (block?: { payload?: any } | null): boolean =>
  block?.payload?.viewer?.allow_print === true;

/**
 * Download permission is per file too: the reader saves one file at a time,
 * never the whole book. Same shape and default as print.
 * Mirrors modules/Book/Services/FileDownloadSettings.php on the API.
 */
export const DOWNLOADABLE_CONTENT_TYPES = ['pdf'] as const;

export const isDownloadableContentType = (contentType?: string): boolean =>
  !!contentType && (DOWNLOADABLE_CONTENT_TYPES as readonly string[]).includes(contentType);

export const isFileDownloadAllowed = (block?: { payload?: any } | null): boolean =>
  block?.payload?.viewer?.allow_download === true;

/**
 * Whether students get this block at all. Any content type can be hidden; the
 * API strips hidden blocks from every payload served to non-teacher accounts,
 * so the reader app never sees them. Missing flag (older blocks) means visible.
 * Mirrors modules/Book/Services/StudentVisibility.php on the API.
 */
export const isVisibleToStudents = (block?: { payload?: any } | null): boolean =>
  block?.payload?.viewer?.visible_to_students !== false;

/**
 * How many blocks in a section — and everything under it — students never
 * receive. A lesson can look complete in the composer while reaching students
 * with nothing in it, so chapter and lesson headers say so.
 */
export const countHiddenFromStudents = (section?: any): number => {
  if (!section) return 0;

  // Bài gộp thắng khối đơn, đúng như composer dựng: một bài từng là khối đơn rồi
  // được gộp vẫn còn `content_block` cũ mà không màn nào hiện, đếm vào thì con số
  // không bao giờ về 0 dù đã bật lại hết công tắc.
  const blocks: any[] = (
    Array.isArray(section.content_blocks) && section.content_blocks.length > 0
      ? section.content_blocks
      : [section.content_block]
  ).filter(Boolean);

  // Chapters expose their lessons as `sections`, lessons their parts as `children`
  const children: any[] = [
    ...(Array.isArray(section.children) ? section.children : []),
    ...(Array.isArray(section.sections) ? section.sections : []),
  ];

  return (
    blocks.filter((block: any) => !isVisibleToStudents(block)).length +
    children.reduce(
      (total: number, child: any) => total + countHiddenFromStudents(child),
      0
    )
  );
};

/** Section-level switch: false = the reader app never receives this chapter/lesson (any role). */
export const isVisibleToReaders = (section?: { visible_to_readers?: boolean } | null): boolean =>
  section?.visible_to_readers !== false;

/** Handler shape for the section-level reader switch. */
export type SetReaderVisible = (sectionId: string, visible: boolean) => void;

/** Per-block boolean options stored under `payload.viewer` and toggled from the block header. */
export type ViewerOption = 'allow_print' | 'allow_download' | 'visible_to_students';

/** Handler shape shared by every component that renders the block header switches. */
export type SetViewerOption = (
  sessionId: string,
  blockIndex: number | undefined,
  option: ViewerOption,
  value: boolean
) => void;

export interface Book {
  id: string;
  title: string;
  description?: string;
  author?: string;
  category?: string;
  status: 'draft' | 'published' | 'archived';
  cover_image_url?: string;
  createdAt?: string;
  updatedAt?: string;
  contents: BookSection[]; // Root sections (chapters)
}

export interface BookSection {
  id: string;
  title: string;
  order: number;
  type: 'chapter' | 'container' | 'file' | 'content' | 'text' | 'image' | 'pdf' | 'video' | 'audio' | 'interactive' | 'quiz';
  duration?: number;
  start_day_offset?: number;
  start_day_offset_relative_to?: 'book' | 'chapter' | 'section';
  start_day_offset_reference_id?: string;
  file_id?: string;
  file?: {
    file_id: string;
    file_name: string;
    file_type: string;
    file_size: number;
    file_url: string;
    file_path?: string;
  };
  content?: SectionContent;
  assessment?: any;
  requirements?: any;
  book_id?: string;
  parent_section_id?: string; // For hierarchical structure
  children?: BookSection[]; // Child sections
  created_by?: string;
  updated_by?: string;
  content_block?: ContentBlock;
  content_blocks?: ContentBlockWithLayout[]; // Array of content blocks for merged sections
  access?: AccessControl;
  /** Tắt (false) thì app đọc sách không nhận mục này, với mọi vai trò. Thiếu = hiện. */
  visible_to_readers?: boolean;
  progress_rules?: ProgressRules;
  estimates?: TimeEstimates;
  created_at?: string;
  updated_at?: string;
}

export interface ContentBlock {
  content_type: 'html' | 'pdf' | 'scorm' | 'html_package' | 'pptx' | 'media' | 'quiz' | 'video' | 'audio' | 'image' | 'embed' | 'list' | 'text' | 'flashcard';
  payload: any;
}

export interface ContentBlockWithLayout {
  content_type: 'html' | 'pdf' | 'scorm' | 'html_package' | 'pptx' | 'media' | 'quiz' | 'video' | 'audio' | 'image' | 'embed' | 'list' | 'text' | 'flashcard';
  payload: any;
  appearance?: {
    background?: {
      color: string;
      image?: string | null;
      fit?: 'cover' | 'contain' | 'fill';
      opacity?: number;
    };
    padding?: {
      top?: string;
      bottom?: string;
      left?: string;
      right?: string;
    };
  };
  layout?: {
    mode: 'flow' | 'fixed' | 'grid';
  };
  timing?: {
    startMs?: number;
    endMs?: number | null;
    autoplay?: boolean;
  };
}

export interface AccessControl {
  visibility: 'public' | 'registered' | 'paid';
  is_free_preview: boolean;
  unlock_conditions: string[];
}

export interface ProgressRules {
  completion_type: 'viewed' | 'scorm_report' | 'quiz_pass';
  passing_score?: number;
}

export interface TimeEstimates {
  reading_time_min: number;
}

export interface SectionContent {
  // For text content
  text?: string;
  
  // For file-based content (PDF, video, audio)
  file?: {
    id: string;
    name: string;
    type: string;
    size: number;
    url: string;
    file_path?: string;
  };
  
  // For image content
  image?: {
    id: string;
    url: string;
    alt: string;
    caption?: string;
  };
  
  // For interactive content
  interactive?: {
    type: 'quiz' | 'exercise' | 'game';
    data: any;
  };
}

// Simplified interfaces for creating new items
export interface CreateBook {
  title: string;
  description?: string;
  author?: string;
  category?: string;
}

export interface CreateBookSection {
  title: string;
  order: number;
  type: 'chapter' | 'container' | 'file' | 'content' | 'text' | 'image' | 'pdf' | 'video' | 'audio' | 'interactive' | 'quiz';
  duration?: number;
  content?: Partial<SectionContent>;
  parent_section_id?: string;
  book_id?: string;
  children?: BookSection[]; // Add support for children in creation
  access?: AccessControl;
  progress_rules?: ProgressRules;
  estimates?: TimeEstimates;
  content_block?: ContentBlock;
  content_blocks?: ContentBlockWithLayout[];
}

// Update interfaces
export interface UpdateBook extends Partial<CreateBook> {
  status?: 'draft' | 'published' | 'archived';
}

export interface UpdateBookSection extends Partial<CreateBookSection> {}

// Legacy interfaces for backward compatibility (can be removed later)
export interface Chapter extends BookSection {}
export interface Section extends BookSection {}
export interface CreateChapter extends CreateBookSection {}
export interface CreateSection extends CreateBookSection {}
export interface UpdateChapter extends UpdateBookSection {}
export interface UpdateSection extends UpdateBookSection {}
